import {
  ListKPIsResponse,
  ListKPIsResponse_KPI,
} from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/aqora_service_pb';
import { MetricType } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/metrics_pb';
import {
  KpiModel,
  KPI_V2_DRAINED_TOTAL,
  SyntheticMetricType,
} from '~/pages/Dashboard/models/KpiModel';
import { getRoundedKpiValue } from '~/pages/Dashboard/utils/kpi-calculations';

export type SystemAutomationMode = 'outsideGrowing' | 'growing';
export type KpiGroupMode = SystemAutomationMode | 'manual';

export type KpiGroup = {
  automationMode: KpiGroupMode;
  incomingWaterKpis: KpiModel[];
  outgoingWaterKpis: KpiModel[];
};

export type AutomationModeKpiGroup = KpiGroup & {
  automationMode: SystemAutomationMode;
};

type KpiType = MetricType | SyntheticMetricType;
type KpiGroupTypes = {
  incoming: readonly KpiType[];
  outgoing: readonly KpiType[];
};
type GetLiters = (type: KpiType) => number | undefined;

const AUTOMATION_MODES: SystemAutomationMode[] = ['outsideGrowing', 'growing'];

const AUTOMATION_MODE_KPI_TYPES: KpiGroupTypes = {
  incoming: [
    MetricType.KPI_V2_RAIN_TOTAL_L,
    MetricType.KPI_V2_SOIL_LOSS_L,
    MetricType.KPI_V2_TAP_USAGE_L,
  ],
  outgoing: [
    MetricType.KPI_V2_PLANT_USAGE_L,
    KPI_V2_DRAINED_TOTAL,
    MetricType.KPI_V2_OVERFLOW_L,
  ],
};

const MANUAL_KPI_TYPES: KpiGroupTypes = {
  incoming: [MetricType.KPI_V2_RAIN_TOTAL_L, MetricType.KPI_V2_SOIL_LOSS_L],
  outgoing: [MetricType.KPI_V2_PLANT_USAGE_L],
};

const MANUAL_THRESHOLD_KPI_TYPES = [
  MetricType.KPI_V2_RAIN_TOTAL_L,
  MetricType.KPI_V2_PLANT_USAGE_L,
];

// The manual group is shown once its share of the total exceeds this ratio.
const MANUAL_SHARE_THRESHOLD = 0.1;

function isKpiTypeAvailable(type: KpiType, availableMetricTypes: MetricType[]) {
  if (type === KPI_V2_DRAINED_TOTAL) {
    return (
      availableMetricTypes.includes(MetricType.KPI_V2_DRAINED_DELAYED_L) ||
      availableMetricTypes.includes(MetricType.KPI_V2_DRAINED_DIRECT_L)
    );
  }

  return availableMetricTypes.includes(type);
}

function getValidLiters(kpis: ListKPIsResponse_KPI[], type: MetricType) {
  const kpi = kpis.find((item) => item.type === type);

  return kpi?.valid ? kpi.value : undefined;
}

function toKpiValue(liters: number) {
  const roundedUsage = getRoundedKpiValue(liters);

  return roundedUsage > 0 ? roundedUsage : 0;
}

function getModeLiters(
  kpis: ListKPIsResponse_KPI[],
  availableMetricTypes: MetricType[],
): GetLiters {
  return (type) => {
    if (type !== KPI_V2_DRAINED_TOTAL) {
      return getValidLiters(kpis, type);
    }

    const [delayed, direct] = [
      MetricType.KPI_V2_DRAINED_DELAYED_L,
      MetricType.KPI_V2_DRAINED_DIRECT_L,
    ].map((drainedType) =>
      availableMetricTypes.includes(drainedType)
        ? getValidLiters(kpis, drainedType)
        : undefined,
    );

    return delayed === undefined && direct === undefined
      ? undefined
      : (delayed ?? 0) + (direct ?? 0);
  };
}

function createKpiGroup<TMode extends KpiGroupMode>(
  automationMode: TMode,
  kpiTypes: KpiGroupTypes,
  availableMetricTypes: MetricType[],
  getLiters: GetLiters,
): (KpiGroup & { automationMode: TMode }) | null {
  const toKpis = (types: readonly KpiType[]) =>
    types
      .filter((type) => isKpiTypeAvailable(type, availableMetricTypes))
      .flatMap((type) => {
        const liters = getLiters(type);

        return liters === undefined
          ? []
          : [new KpiModel(type, toKpiValue(liters))];
      });

  const incomingWaterKpis = toKpis(kpiTypes.incoming);
  const outgoingWaterKpis = toKpis(kpiTypes.outgoing);

  if (incomingWaterKpis.length === 0 && outgoingWaterKpis.length === 0) {
    return null;
  }

  return { automationMode, incomingWaterKpis, outgoingWaterKpis };
}

export class KpiListModel {
  readonly automationModeKpiGroups: AutomationModeKpiGroup[];
  readonly manualKpiGroup: KpiGroup | null;

  constructor(response: ListKPIsResponse, availableMetricTypes: MetricType[]) {
    this.automationModeKpiGroups = AUTOMATION_MODES.flatMap(
      (automationMode) => {
        const group = createKpiGroup(
          automationMode,
          AUTOMATION_MODE_KPI_TYPES,
          availableMetricTypes,
          getModeLiters(response[automationMode], availableMetricTypes),
        );

        return group ? [group] : [];
      },
    );

    // Everything outside the growing and outside growing modes (manual,
    // freezing protection and unspecified). A mode without a valid value
    // counts as zero.
    const getManualLiters: GetLiters = (type) => {
      if (type === KPI_V2_DRAINED_TOTAL) {
        return undefined;
      }

      const total = getValidLiters(response.total, type);

      return total === undefined
        ? undefined
        : total -
            (getValidLiters(response.growing, type) ?? 0) -
            (getValidLiters(response.outsideGrowing, type) ?? 0);
    };

    const exceedsManualShareThreshold = MANUAL_THRESHOLD_KPI_TYPES.filter(
      (type) => isKpiTypeAvailable(type, availableMetricTypes),
    ).some((type) => {
      const total = getValidLiters(response.total, type);
      const manual = getManualLiters(type);

      return (
        total !== undefined &&
        manual !== undefined &&
        total > 0 &&
        manual / total > MANUAL_SHARE_THRESHOLD
      );
    });

    this.manualKpiGroup = exceedsManualShareThreshold
      ? createKpiGroup(
          'manual',
          MANUAL_KPI_TYPES,
          availableMetricTypes,
          getManualLiters,
        )
      : null;
  }
}
